import React from 'react';
import { PlayerInfo } from '../types/game';
import { NeonButton } from './NeonButton';
import { useSound } from '../hooks/useSound';

interface PlayerListProps {
  players: PlayerInfo[];
  onInvitePlayer: (playerId: string) => void;
  currentPlayerId: string;
}

export const PlayerList: React.FC<PlayerListProps> = ({
  players,
  onInvitePlayer,
  currentPlayerId
}) => {
  const { playSound } = useSound();

  const getStatusColor = (status: PlayerInfo['status']) => {
    switch (status) {
      case 'available':
        return 'border-neon-green text-neon-green';
      case 'playing':
        return 'border-neon-yellow text-neon-yellow';
      case 'in-game':
        return 'border-neon-pink text-neon-pink';
      default:
        return 'border-gray-500 text-gray-500';
    }
  };

  const getStatusText = (status: PlayerInfo['status']) => {
    switch (status) {
      case 'available':
        return 'DISPONIBLE';
      case 'playing':
        return 'ESPERANDO';
      case 'in-game':
        return 'JUGANDO';
      default:
        return 'DESCONOCIDO';
    }
  };

  const getStatusIcon = (status: PlayerInfo['status']) => {
    switch (status) {
      case 'available':
        return '🟢';
      case 'playing':
        return '🟡';
      case 'in-game':
        return '🔴';
      default:
        return '⚪';
    }
  };

  const canInvite = (player: PlayerInfo) => {
    return player.status === 'available' && player.id !== currentPlayerId;
  };

  return (
    <div className="ui-section">
      <div className="ui-card">
        <h2 className="ui-title">
          JUGADORES EN LÍNEA
        </h2>
        
        {players.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-4xl mb-4">👻</div>
            <p className="font-retro text-gray-400">
              No hay otros jugadores conectados
            </p>
            <p className="font-retro text-gray-500 text-sm mt-2">
              Espera a que alguien se una...
            </p>
          </div>
        ) : (
          <div className="space-y-2 sm:space-y-3 max-h-96 overflow-y-auto">
            {players.map((player) => (
              <div
                key={player.id}
                className={`
                  flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 rounded-lg border-2
                  bg-retro-black/50 transition-all duration-300
                  hover:bg-retro-black/70
                  ${getStatusColor(player.status)}
                `}
              >
                <div className="flex items-center space-x-3 flex-1 w-full">
                  <div className="text-xl">
                    {getStatusIcon(player.status)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-retro font-bold truncate">
                      {player.name}
                    </div>
                    <div className="text-[10px] sm:text-xs opacity-75">
                      Jugador {player.symbol} • {getStatusText(player.status)}
                    </div>
                  </div>
                </div>
                
                {canInvite(player) && (
                  <NeonButton
                    onClick={() => {
                      playSound('click');
                      onInvitePlayer(player.id);
                    }}
                    color="green"
                    className="w-full sm:w-auto px-4 py-2 text-xs sm:text-sm"
                    disabled={!canInvite(player)}
                  >
                    INVITAR
                  </NeonButton>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
